
import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { canUserSendCopilotMessage } from '@/services/subscriptionService';
import { PROMPT_ARCHITECT_ENHANCED_SYSTEM } from '@/config/promptArchitectSystemEnhanced';

export interface ChatMessage {
  id: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  session_id: string;
  timestamp: string;
  isEnhanced?: boolean;
}

export const useProductCopilotChatSession = (
  sessionId: string | null,
  templateId: string | null,
  userId: string | null
) => {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [promptContent, setPromptContent] = useState('');
  const [currentSessionId, setCurrentSessionId] = useState<string | null>(sessionId);
  const [currentSessionMode, setCurrentSessionMode] = useState<string | null>(null);

  const resetChatState = useCallback(() => {
    setMessages([]);
    setPromptContent('');
    setCurrentSessionId(null);
    setCurrentSessionMode(null);
  }, []);

  // Load existing session
  useEffect(() => {
    if (sessionId && sessionId !== currentSessionId) {
      loadSession(sessionId);
    }
  }, [sessionId, currentSessionId]);

  const loadSession = async (id: string) => {
    try {
      setIsLoading(true);
      
      // Load session details
      const { data: sessionData, error: sessionError } = await supabase
        .from('product_chat_sessions')
        .select('*, prompts(title, content)')
        .eq('id', id)
        .single();

      if (sessionError) throw sessionError;

      // Set session mode based on template presence
      if (sessionData.template_id && sessionData.prompts) {
        setPromptContent(sessionData.prompts.content);
        setCurrentSessionMode('template');
      } else {
        // Check if it's an enhance session by looking at the title
        if (sessionData.title && sessionData.title.includes('Enhanced:')) {
          setCurrentSessionMode('enhance');
        } else {
          setCurrentSessionMode('free');
        }
      }

      // Load messages
      const { data: messagesData, error: messagesError } = await supabase
        .from('product_chat_messages')
        .select('*')
        .eq('session_id', id)
        .order('created_at', { ascending: true });

      if (messagesError) throw messagesError;

      const formattedMessages: ChatMessage[] = messagesData.map((msg) => ({
        id: msg.id,
        role: msg.role as 'user' | 'assistant' | 'system',
        content: msg.content,
        session_id: msg.session_id,
        timestamp: msg.created_at,
        isEnhanced: Boolean(msg.role === 'assistant' && (sessionData.template_id || currentSessionMode === 'enhance'))
      }));

      setMessages(formattedMessages);
      setCurrentSessionId(id);
    } catch (error) {
      console.error('Error loading session:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const createSession = async (firstMessage: string, mode: string, templateId?: string) => {
    if (!userId) return null;

    try {
      // Generate smart title based on first message and mode
      const title = generateSessionTitle(firstMessage, mode);
      
      const { data, error } = await supabase
        .from('product_chat_sessions')
        .insert({
          user_id: userId,
          title,
          template_id: templateId || null
        })
        .select()
        .single();

      if (error) throw error;

      setCurrentSessionId(data.id);
      setCurrentSessionMode(mode);
      return data.id;
    } catch (error) {
      console.error('Error creating session:', error);
      return null;
    }
  };

  const generateSessionTitle = (message: string, mode: string) => {
    const truncated = message.length > 50 ? message.substring(0, 50) + '...' : message;
    
    switch (mode) {
      case 'enhance':
        return `Enhanced: ${truncated}`;
      case 'template':
        return `Template: ${truncated}`;
      default:
        return truncated;
    }
  };

  const saveMessage = async (sessionId: string, role: string, content: string) => {
    try {
      const { data, error } = await supabase
        .from('product_chat_messages')
        .insert({
          session_id: sessionId,
          role,
          content
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error saving message:', error);
      return null;
    }
  };

  const needsNewSession = (requestedMode: string) => {
    // Create new session if:
    // 1. No current session exists
    // 2. Mode is changing from current session mode
    if (!currentSessionId || !currentSessionMode) return true;
    
    return currentSessionMode !== requestedMode;
  };

  const sendMessage = async (content: string, mode: string = 'free', templateContent?: string) => {
    if (!userId || !content.trim()) return;

    try {
      // Check usage limits
      const usageCheck = await canUserSendCopilotMessage(userId);
      if (!usageCheck.canSend) {
        return;
      }

      setIsLoading(true);

      // Check if we need a new session due to mode change
      let sessionId = currentSessionId;
      if (needsNewSession(mode)) {
        // Reset current state for new session
        setMessages([]);
        sessionId = await createSession(content, mode, templateId);
        if (!sessionId) return;
      }

      // Add user message to UI immediately
      const userMessage: ChatMessage = {
        id: `temp-${Date.now()}`,
        role: 'user',
        content,
        session_id: sessionId,
        timestamp: new Date().toISOString()
      };

      setMessages(prev => [...prev, userMessage]);

      // Save user message to database
      await saveMessage(sessionId, 'user', content);

      // Call the appropriate API based on mode
      let apiResponse;
      
      if (mode === 'enhance') {
        // For enhance mode, call OpenAI with the enhanced system prompt
        const { data, error } = await supabase.functions.invoke('openai-chat', {
          body: {
            userMessage: content,
            userId: userId,
            sessionId: sessionId,
            isEnhancement: true,
            enhancementSystemPrompt: PROMPT_ARCHITECT_ENHANCED_SYSTEM,
            history: messages.map(m => ({
              role: m.role,
              content: m.content
            }))
          }
        });

        if (error) throw error;
        apiResponse = data.response;
      } else {
        // For other modes, use regular chat
        const { data, error } = await supabase.functions.invoke('openai-chat', {
          body: {
            userMessage: content,
            userId: userId,
            sessionId: sessionId,
            promptTemplate: mode === 'template' ? templateContent : undefined,
            history: messages.map(m => ({
              role: m.role,
              content: m.content
            }))
          }
        });

        if (error) throw error;
        apiResponse = data.response;
      }

      // Add AI response to UI
      const aiMessage: ChatMessage = {
        id: `ai-${Date.now()}`,
        role: 'assistant',
        content: apiResponse || 'No response received',
        session_id: sessionId,
        timestamp: new Date().toISOString(),
        isEnhanced: mode === 'enhance' || mode === 'template'
      };

      setMessages(prev => [...prev, aiMessage]);

      // Save AI message to database
      await saveMessage(sessionId, 'assistant', apiResponse || 'No response received');

      // Increment usage
      await supabase.rpc('increment_copilot_chat_usage', { p_user_id: userId });

    } catch (error: any) {
      console.error('Error sending message:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const addEnhancedMessage = useCallback((message: ChatMessage) => {
    setMessages(prev => [...prev, message]);
  }, []);

  return {
    messages,
    isLoading,
    sessionId: currentSessionId,
    promptContent,
    currentSessionMode,
    sendMessage,
    addEnhancedMessage,
    resetChatState
  };
};
